import { memo, ReactNode } from 'react';
import { SpaceBetween } from '@amzn/awsui-components-core';

import { SpecialSectionId } from '../../services/SectionOperations';
import SectionEditorControl from '../v2-common/SectionEditorControl';
import { SectionScrollTarget } from '../v2-common/SectionScrollTarget';
import { PatternEpicEditor } from './PatternEpicEditor';
import { PatternTroubleshootingEditor } from './PatternTroubleshootingEditor';
import { usePatternEditorViewModel } from './PatternEditorViewModel';
import { useScrollToCurrentPatternSection } from '../v2-hooks/useScrollToCurrentPatternSection';
import { PATTERN_SECTION_INFO } from './PatternSectionInfo';
import { PatternSectionInfoLink } from './PatternSectionInfoLink';

/** Editor surface for Pattern content. Renders the Pattern's flat list of sections. */
const PatternEditor = memo(function PatternEditor() {
  const vm = usePatternEditorViewModel();
  useScrollToCurrentPatternSection(vm?.sections ?? []);

  if (!vm) return null;

  return (
    <SpaceBetween size="l">
      {vm.sections.map((section) => {
        const sectionInfo = PATTERN_SECTION_INFO[section.id];
        const description = sectionInfo?.description;
        const info = sectionInfo && <PatternSectionInfoLink topicId={sectionInfo.topicId} />;
        let editor: ReactNode;
        if (section.id === SpecialSectionId.Epics) {
          editor = (
            <PatternEpicEditor
              node={section}
              contentId={vm.contentId}
              description={description}
              info={info}
            />
          );
        } else if (section.id === SpecialSectionId.Troubleshooting) {
          editor = (
            <PatternTroubleshootingEditor
              node={section}
              contentId={vm.contentId}
              description={description}
              info={info}
            />
          );
        } else {
          editor = (
            <SectionEditorControl
              node={section}
              contentId={vm.contentId}
              title={section.title}
              description={description}
              info={info}
            />
          );
        }

        return (
          <SectionScrollTarget key={section.id} node={section}>
            {editor}
          </SectionScrollTarget>
        );
      })}
    </SpaceBetween>
  );
});

export default PatternEditor;
